import { isDirectVideoUrl } from "@elaso/shared/utils/video";
import { X } from "lucide-react";
import { Button } from "./button";
import { Image } from "./image";

type OpenGraphData = {
	title?: string;
	description?: string;
	image?: string;
	imageWidth?: number;
	imageHeight?: number;
	url: string;
	siteName?: string;
	twitterCard?: string;
	video?: string;
	videoType?: string;
	videoWidth?: number;
	videoHeight?: number;
};

const VIMEO_ID_REGEX = /^\d+$/;

function getVideoEmbedUrl(url: string): string | null {
	try {
		const urlObj = new URL(url);

		if (
			urlObj.hostname === "www.youtube.com" ||
			urlObj.hostname === "youtube.com"
		) {
			const videoId = urlObj.searchParams.get("v");
			if (videoId) {
				return `https://www.youtube.com/embed/${videoId}`;
			}
		}

		if (urlObj.hostname === "youtu.be") {
			const videoId = urlObj.pathname.slice(1);
			if (videoId) {
				return `https://www.youtube.com/embed/${videoId}`;
			}
		}

		if (
			urlObj.hostname === "www.vimeo.com" ||
			urlObj.hostname === "vimeo.com"
		) {
			const videoId = urlObj.pathname.split("/").pop();
			if (videoId && VIMEO_ID_REGEX.test(videoId)) {
				return `https://player.vimeo.com/video/${videoId}`;
			}
		}

		return null;
	} catch {
		return null;
	}
}

// Pick the large layout from the Twitter card tag, falling back to image size.
function shouldUseLargeLayout(embed: OpenGraphData): boolean {
	// 1. The Twitter card meta tag, when the page sets one.
	if (embed.twitterCard === "summary_large_image") {
		return true;
	}
	if (embed.twitterCard === "summary") {
		return false;
	}

	// 2. Otherwise, guess from the image dimensions.
	if (embed.imageWidth && embed.imageHeight) {
		if (embed.imageWidth > 400) {
			// A square image looks wrong large, so exclude a near 1:1 ratio.
			const aspectRatio = embed.imageWidth / embed.imageHeight;
			if (Math.abs(aspectRatio - 1) < 0.1) {
				return false;
			}
			return true;
		}
		return false;
	}

	// 3. With an image but no dimensions, default to the large layout.
	return !!embed.image;
}

type MessageEmbedsProps = {
	embedData?: OpenGraphData[] | null;
	onSuppressEmbeds?: () => void;
	suppressedEmbeds?: boolean;
};

export function MessageEmbeds({
	embedData,
	onSuppressEmbeds,
	suppressedEmbeds,
}: MessageEmbedsProps) {
	if (suppressedEmbeds || !embedData || embedData.length === 0) {
		return null;
	}

	return (
		<div className="mt-2 flex flex-col gap-[10px]">
			{embedData.map((embed, index) => {
				const isDirectVideo = isDirectVideoUrl(embed.url);
				const videoEmbedUrl =
					embed.video && embed.videoType === "text/html"
						? embed.video
						: getVideoEmbedUrl(embed.url);
				const hasEmbeddableVideo = videoEmbedUrl !== null && !isDirectVideo;
				const useLargeLayout = shouldUseLargeLayout(embed);

				if (isDirectVideo) {
					return (
						<div className="group relative w-fit" key={`${embed.url}-${index}`}>
							{onSuppressEmbeds && (
								<Button
									className="-top-3 -right-3 absolute z-20 size-6 rounded-full border border-border bg-secondary opacity-0 shadow-sm transition-all duration-200 hover:bg-destructive hover:text-destructive-foreground group-hover:opacity-100"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										onSuppressEmbeds();
									}}
									size="icon"
									title="Remove embed"
									variant="secondary"
								>
									<X className="size-3" />
								</Button>
							)}
							<video
								className="max-h-96 max-w-lg rounded-lg"
								controls
								src={embed.url}
							>
								<track kind="captions" />
							</video>
						</div>
					);
				}

				if (hasEmbeddableVideo) {
					return (
						<div className="group relative w-fit" key={`${embed.url}-${index}`}>
							{onSuppressEmbeds && (
								<Button
									className="-top-3 -right-3 absolute z-20 size-6 rounded-full border border-border bg-secondary opacity-0 shadow-sm transition-all duration-200 hover:bg-destructive hover:text-destructive-foreground group-hover:opacity-100"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										onSuppressEmbeds();
									}}
									size="icon"
									title="Remove embed"
									variant="secondary"
								>
									<X className="size-3" />
								</Button>
							)}
							<div className="flex max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-secondary/30">
								<div className="aspect-video w-full min-w-[400px]">
									<iframe
										allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
										allowFullScreen
										className="h-full w-full"
										referrerPolicy="strict-origin-when-cross-origin"
										src={videoEmbedUrl}
										title={embed.title || "Video"}
									/>
								</div>
								<a
									className="block p-3 no-underline transition-colors duration-200 hover:bg-secondary/50"
									href={embed.url}
									rel="noopener noreferrer"
									target="_blank"
								>
									{embed.siteName && (
										<div className="mb-1 font-medium text-muted-foreground text-xs">
											{embed.siteName}
										</div>
									)}
									{embed.title && (
										<div className="line-clamp-2 font-semibold text-foreground text-sm">
											{embed.title}
										</div>
									)}
								</a>
							</div>
						</div>
					);
				}

				return (
					<div className="group relative w-fit" key={`${embed.url}-${index}`}>
						{onSuppressEmbeds && (
							<Button
								className="-top-3 -right-3 absolute z-20 size-6 rounded-full border border-border bg-secondary opacity-0 shadow-sm transition-all duration-200 hover:bg-destructive hover:text-destructive-foreground group-hover:opacity-100"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									onSuppressEmbeds();
								}}
								size="icon"
								title="Remove embed"
								variant="secondary"
							>
								<X className="size-3" />
							</Button>
						)}

						<a
							className={`flex max-w-lg overflow-hidden rounded-lg border border-border bg-secondary/30 no-underline transition-colors duration-200 hover:bg-secondary/50 ${
								useLargeLayout && embed.image ? "flex-col" : "flex-row"
							}`}
							href={embed.url}
							rel="noopener noreferrer"
							target="_blank"
						>
							{useLargeLayout && embed.image && (
								<div className="w-full flex-shrink-0 bg-muted/30">
									<Image
										alt={embed.title || "Preview"}
										className="max-h-96 w-full object-contain"
										onError={(e) => {
											e.currentTarget.parentElement?.remove();
										}}
										src={embed.image}
									/>
								</div>
							)}

							<div
								className={`min-w-0 flex-1 p-3 ${embed.image ? "" : "min-w-[300px]"}`}
							>
								{embed.siteName && (
									<div className="mb-1 font-medium text-muted-foreground text-xs">
										{embed.siteName}
									</div>
								)}

								{embed.title && (
									<div className="mb-1 line-clamp-2 font-semibold text-foreground text-sm">
										{embed.title}
									</div>
								)}

								{embed.description && (
									<div className="line-clamp-2 text-muted-foreground text-xs">
										{embed.description}
									</div>
								)}
							</div>

							{!useLargeLayout && embed.image && (
								<div className="h-24 w-24 flex-shrink-0 bg-muted/30">
									<Image
										alt={embed.title || "Preview"}
										className="h-full w-full object-contain"
										onError={(e) => {
											e.currentTarget.parentElement?.remove();
										}}
										src={embed.image}
									/>
								</div>
							)}
						</a>
					</div>
				);
			})}
		</div>
	);
}
